import React, { useEffect, useState } from "react";
import { Trash2, Pencil, X, Eye, EyeOff } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const EMPTY = { title: "", slug: "", excerpt: "", content: "", cover_image: "", tags: "", read_time: 5, published: true };
const field = "w-full bg-white border border-[#E5E5E5] p-2.5 font-body text-xs text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#2E3A9E] rounded-md";

export default function AdminBlog({ notify }) {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");

  const load = () => api.get("/blog", { params: { all: true } }).then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const toPayload = (f) => ({ ...f, read_time: Number(f.read_time) || 5, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editId) await api.put(`/blog/${editId}`, toPayload(form)); else await api.post("/blog", toPayload(form));
      setForm(EMPTY); setEditId(null); load(); notify(editId ? "Article updated" : "Article published");
    } catch (err) { setError(formatApiError(err)); }
  };

  const edit = (p) => { setEditId(p.id); setForm({ title: p.title, slug: p.slug, excerpt: p.excerpt, content: p.content, cover_image: p.cover_image || "", tags: (p.tags || []).join(", "), read_time: p.read_time || 5, published: p.published }); };
  const togglePublish = async (p) => { await api.put(`/blog/${p.id}`, { ...p, published: !p.published }); load(); };
  const remove = async (id) => { if (!window.confirm("Delete this article?")) return; await api.delete(`/blog/${id}`); load(); notify("Article deleted"); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="admin-blog">
      <form onSubmit={submit} className="lg:col-span-5 bg-white border border-[#E5E5E5] p-6 rounded-md h-fit shadow-sm relative space-y-3">
        <div className="absolute top-0 left-0 w-full h-1 bg-[#2E3A9E]" />
        <div className="flex justify-between items-center">
          <h4 className="font-heading text-lg font-bold">{editId ? "Edit Article" : "New Article"}</h4>
          {editId && <button type="button" onClick={() => { setEditId(null); setForm(EMPTY); }} className="text-gray-400 hover:text-black"><X size={16} /></button>}
        </div>
        <input data-testid="admin-blog-title-input" required placeholder="Title" value={form.title} onChange={set("title")} className={field} />
        <input data-testid="admin-blog-slug-input" placeholder="Slug (auto from title if blank)" value={form.slug} onChange={set("slug")} className={field} />
        <textarea data-testid="admin-blog-excerpt-input" required rows="2" placeholder="Excerpt" value={form.excerpt} onChange={set("excerpt")} className={field} />
        <textarea data-testid="admin-blog-content-input" required rows="10" placeholder="Content (Markdown supported: ## headings, **bold**, - lists)" value={form.content} onChange={set("content")} className={`${field} font-mono`} />
        <input data-testid="admin-blog-cover-input" placeholder="Cover image URL" value={form.cover_image} onChange={set("cover_image")} className={field} />
        <div className="grid grid-cols-3 gap-3">
          <input data-testid="admin-blog-tags-input" placeholder="Tags, comma separated" value={form.tags} onChange={set("tags")} className={`${field} col-span-2`} />
          <input data-testid="admin-blog-readtime-input" type="number" min="1" placeholder="Min" value={form.read_time} onChange={set("read_time")} className={field} />
        </div>
        <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-gray-600">
          <input data-testid="admin-blog-published-checkbox" type="checkbox" checked={form.published} onChange={set("published")} /> Published
        </label>
        {error && <p data-testid="admin-blog-error" className="font-body text-xs text-red-600">{error}</p>}
        <button data-testid="admin-blog-submit-btn" className="w-full font-body font-bold text-xs uppercase tracking-wide py-3 bg-[#2E3A9E] text-white hover:bg-[#232D7C] rounded-md">
          {editId ? "Save changes" : "Publish article"}
        </button>
      </form>

      <div className="lg:col-span-7 space-y-3">
        {posts.map((p) => (
          <div key={p.id} data-testid="admin-blog-row" className="bg-white border border-[#E5E5E5] p-5 rounded-md flex gap-4 justify-between items-start">
            <div className="min-w-0 space-y-1">
              <p className="font-heading text-sm font-bold text-[#0A0A0A]">{p.title}</p>
              <p className="font-mono text-[10px] text-gray-500">/blog/{p.slug} · {p.read_time} min · {(p.tags || []).join(", ")}</p>
              <span className={`inline-block font-mono text-[9px] uppercase px-2 py-0.5 rounded border ${p.published ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-50 text-gray-500 border-gray-200"}`}>{p.published ? "Published" : "Draft"}</span>
            </div>
            <div className="flex gap-2 shrink-0">
              <button data-testid="admin-blog-toggle-btn" onClick={() => togglePublish(p)} title={p.published ? "Unpublish" : "Publish"} className="p-2 border border-gray-200 text-gray-600 hover:bg-gray-50 rounded">{p.published ? <EyeOff size={14} /> : <Eye size={14} />}</button>
              <button data-testid="admin-blog-edit-btn" onClick={() => edit(p)} className="p-2 border border-gray-200 text-gray-600 hover:bg-gray-50 rounded"><Pencil size={14} /></button>
              <button data-testid="admin-blog-delete-btn" onClick={() => remove(p.id)} className="p-2 border border-red-200 text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {posts.length === 0 && <p className="font-mono text-xs text-gray-400 text-center py-10">NO ARTICLES YET.</p>}
      </div>
    </div>
  );
}
